import React, { useEffect, useState } from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePickerProps } from './types/DatePickerTypes';
import { lt } from 'date-fns/locale';

/**
 * A date picker component that wraps MUI's DatePicker.
 * Provides a simple interface for selecting dates with a calendar popup.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
  className,
  InputProps,
  minDate,
  maxDate,
  ...rest
}) => {
  const [date, setDate] = useState<Date | null>(() => {
    // Convert string date to Date object or null
    return value ? new Date(value) : null;
  });

  useEffect(() => {
    // Update the state when value prop changes
    setDate(value ? new Date(value) : null);
  }, [value]);

  const handleDateChange = (newDate: Date | null) => {
    setDate(newDate);
    // Create a synthetic change event to match the expected interface
    const syntheticEvent = {
      target: {
        name,
        value: newDate ? newDate instanceof Date && !isNaN(newDate.getTime()) ? 
          formatDateToUTC(newDate) : 
          '' : ''
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);
  };

  // Helper function to format date to YYYY-MM-DD in UTC
  const formatDateToUTC = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert string dates to Date objects if provided
  const minDateObj = minDate ? new Date(minDate) : undefined;
  const maxDateObj = maxDate ? new Date(maxDate) : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <MuiDatePicker
        value={date}
        onChange={handleDateChange}
        label={label}
        minDate={minDateObj}
        maxDate={maxDateObj}
        format='yyyy-MM-dd'
        slotProps={{
          textField: {
            id,
            name,
            error,
            helperText: helperText,
            disabled,
            className,
            InputProps,
            fullWidth: true,
            variant: "outlined",
            ...rest
          }
        }}
      />
    </LocalizationProvider>
  );
}; 